import { z } from 'zod';
import { painSchema } from './pain';
import { sectionHasValue } from './util';
import { bloodPressureValidation } from './shared/blood-pressure-validation';
export const snnValidationSchema = z.object({
    id: z.string().optional(),
    data: z.object({
        PlanOfCare: z.boolean().refine((value) => !!value, { message: 'section has errors' }),
        Infections: z.boolean().nullish(),
        Incidents: z.boolean().nullish(),
        SkinConditions: z.object({
            integumentary: z
                .object({
                details: z.array(z.string()).optional(),
                temp: z.string().optional(),
                wnl: z.boolean().optional(),
            })
                .refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
        }),
        MedicationsSupplies: z.boolean().nullish(),
        Vitals: z.object({
            vitals: z
                .object({
                wnl: z.boolean().optional(),
                weight: z.string().optional(),
                height: z.string().optional(),
                temp: z.string().optional(),
                via: z.string().optional(),
            })
                .refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
            o2: z
                .object({
                wnl: z.boolean().optional(),
                o2Sat: z.string().optional(),
                flow: z.string().optional(),
                inr: z.string().optional(),
                usedO2: z.string().optional(),
            })
                .refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
            pulseRespiration: z
                .object({
                wnl: z.boolean().optional(),
                pulse: z.string().optional(),
                aplical: z.string().optional(),
                pulseRegular: z.string().optional(),
                respiration: z.string().optional(),
                respirationRegular: z.string().optional(),
            })
                .refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
            bloodSugar: z
                .object({
                wnl: z.boolean().optional(),
                am: z.string().optional(),
                pm: z.string().optional(),
                noon: z.string().optional(),
                hs: z.string().optional(),
            })
                .refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
            bloodPressure: bloodPressureValidation('notCheckedBloodPressure', {
                checkboxLabel: 'WNL',
            }),
        }),
        Neurosensory: z.object({
            neurological: z
                .object({
                wnl: z.boolean().optional(),
                neurological: z.string().optional(),
                person: z.string().optional(),
                place: z.string().optional(),
                purpose: z.string().optional(),
                time: z.string().optional(),
                details: z.array(z.string()).optional(),
            })
                .refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
            handGrips: z
                .object({
                wnl: z.boolean().optional(),
                equality: z.string().optional(),
                left: z.string().optional(),
                right: z.string().optional(),
            })
                .refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
            extremities: z
                .object({
                wnl: z.boolean().optional(),
                lue: z.string().optional(),
                rue: z.string().optional(),
                lle: z.string().optional(),
                rle: z.string().optional(),
            })
                .refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
            pupils: z
                .object({
                wnl: z.boolean().optional(),
                equality: z.string().optional(),
                reactivity: z.string().optional(),
            })
                .refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
            psychosocial: z
                .object({
                wnl: z.boolean().optional(),
                details: z.array(z.string()).optional(),
            })
                .refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
            pain: z.object({
                none: z.boolean().optional(),
                pains: z.array(painSchema).optional(),
            }),
        }),
        BladderBowel: z.object({
            gastrointestinal: z
                .object({
                wnl: z.boolean().optional(),
                lastBowelMovement: z.string().optional(),
                activity: z.string().optional(),
                details: z.array(z.string()).optional(),
                vomiting: z.string().optional(),
                diarrhea: z.string().optional(),
                abdomenGirth: z.string().optional(),
                abdomenDetail: z.array(z.string()).optional(),
                luq: z.string().optional(),
                ruq: z.string().optional(),
                llq: z.string().optional(),
                rlq: z.string().optional(),
                appetite: z.string().optional(),
                enteralFeeding: z.string().optional(),
                via: z.string().optional(),
                fdgRate: z.string().optional(),
                residual: z.string().optional(),
                ostomy: z.array(z.string()).optional(),
                location: z.string().optional(),
                patent: z.string().optional(),
                amtDrainage: z.string().optional(),
            })
                .refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
            genitourinary: z
                .object({
                wnl: z.boolean().optional(),
                details: z.array(z.string()).optional(),
                catheter: z.string().optional(),
                frSize: z.string().optional(),
                balloonSize: z.string().optional(),
                inserted: z.string().optional(),
                date: z.string().optional(),
                odor: z.string().optional(),
                urineColor: z.string().optional(),
            })
                .refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
        }),
        Respiratory: z.object({
            respiratory: z
                .object({
                wnl: z.boolean().optional(),
                left: z.string().optional(),
                right: z.string().optional(),
                upper: z.string().optional(),
                mid: z.string().optional(),
                base: z.string().optional(),
                shortness: z.string().optional(),
                litersMin1: z.string().optional(),
                lpm1: z.string().optional(),
                litersMin2: z.string().optional(),
                lpm2: z.string().optional(),
                details: z.array(z.string()).optional(),
                detailsText: z.string().optional(),
                continuous: z.string().optional(),
                suctioned: z.string().optional(),
                trach: z.string().optional(),
                cough: z.string().optional(),
                color: z.string().optional(),
                amount: z.string().optional(),
            })
                .refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
        }),
        Cardiovascular: z.object({
            cardiovascular: z
                .object({
                wnl: z.boolean().optional(),
                atRest: z.string().optional(),
                details: z.array(z.string()).optional(),
                lle: z.string().optional(),
                lleStrength: z.string().optional(),
                rle: z.string().optional(),
                rleStrength: z.string().optional(),
                lue: z.string().optional(),
                rue: z.string().optional(),
                edemaLle: z.string().optional(),
                edemaRle: z.string().optional(),
                leftPedal: z.string().optional(),
                rightPedal: z.string().optional(),
                capillaryLess: z.string().optional(),
                refillMore: z.string().optional(),
                homan: z.string().optional(),
            })
                .refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
        }),
        ENTMouth: z.object({
            entMouth: z
                .object({
                wnl: z.boolean().optional(),
                details: z.array(z.string()).optional(),
                visionLeft: z.string().optional(),
                visionRight: z.string().optional(),
                hearingLeft: z.string().optional(),
                hearingRight: z.string().optional(),
            })
                .refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
            endocrine: z
                .object({
                wnl: z.boolean().optional(),
                bs: z.string().optional(),
                doneBy: z.string().optional(),
                fasting: z.string().optional(),
                glycemia: z.string().optional(),
                details: z.array(z.string()).optional(),
            })
                .refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
        }),
        Musculoskeletal: z.object({
            musculoskeletal: z
                .object({
                wnl: z.boolean().optional(),
                gait: z.string().optional(),
                strength: z.string().optional(),
                lue: z.string().optional(),
                rue: z.string().optional(),
                lle: z.string().optional(),
                rle: z.string().optional(),
                fracture: z.string().optional(),
                location: z.string().optional(),
            })
                .refine((data) => sectionHasValue(data), {
                message: 'Mark as within normal limits if none applies',
                path: [],
            }),
        }),
        HomeboundReason: z.object({
            homeboundReasonA: z.string().min(1, 'required'),
            homeboundReasonB: z.string().min(1, 'required'),
            homeboundReasonC: z.string().min(1, 'required'),
        }),
        CoordinationOfCare: z.object({
            physicianNotified: z.array(z.string()).optional(),
            clinicianName: z.string().optional(),
            collaboratorName: z.string().optional(),
            nextVisitPlans: z.string().optional(),
            coordinationDiscipline: z.array(z.string()).optional(),
            coordinationName: z
                .object({
                md: z.string().optional(),
                np: z.string().optional(),
                pa: z.string().optional(),
                sn: z.string().optional(),
                lpn: z.string().optional(),
                pt: z.string().optional(),
                ot: z.string().optional(),
                slp: z.string().optional(),
                hha: z.string().optional(),
            })
                .optional(),
            narrative: z.string().optional(),
        }),
    }),
    signedUserId: z.string().min(1, 'required'),
    signedUserDate: z.string().min(1, 'required'),
});
